/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {createAction} from 'helpers/createAction'
import {UserType} from 'types/enums/UserType'
import {Reservation} from 'types/entities/Reservation'
import ajax from 'helpers/ajax'
import {ReservationDriver} from 'types/entities/ReservationDriver'
import SessionAction from 'store/session/SessionAction'
import {ReservationOfferStatus} from 'types/enums/ReservationOfferStatus'
import {DEFAULT_HALL_ID} from 'helpers/config'
import {ReservationNote} from 'store/booking/BookingAction'
import SellerAction from 'store/seller/SellerAction'
import ReservationsAction from 'store/reservations/ReservationsAction'
import {PackagesCartItem} from 'types/entities/PackagesCartItem'
import {Driver} from 'types/entities/Driver'
import RequestAction from 'store/RequestAction'
import {ReservationsTableRequest} from 'types/requests/ReservationsTableRequest'
import {CompanyContact} from 'types/entities/CompanyContact'

export interface UpdateCustomerFromReservationRequest {
    customer: BookingLoginCustomerInReservation
    reservation: Reservation
}

export interface UpdateCustomerFromReservationNewRequest {
    customer: BookingLoginCustomerInReservationNew
    reservation: Reservation
}

export interface ReservationInvitationRequest {
    email: string
    reservation_id: number
    packages_cart_item_id: number
}

export interface ReservationAssignDriverToItemRequest {
    driver_id: number
    packages_cart_item_id: number
    reservation_id: number
}

export interface ReservationInvitationGuestRequest {
    reservation_driver_guid: string
    name: string
    last_name: string
    nickname: string
    birthdate: string
    address: string
    zip: string
    town: string
    country: string
    email: string
    phone: string
}

export interface ReservationImportGuestsFromFileRequest {
    file: FormData
    reservation_id: number
}

export interface BookingLoginCustomerInReservation {
    driver_id?: number
    user_type: UserType | string
    name: string
    last_name: string
    nickname: string
    birthdate: string
    email?: string
    address?: string
    phone?: string
    reservation_driver_id?: number
}

export interface BookingLoginCustomerInReservationNew {
    driver_id?: number
    user_type: UserType | string
    name: string
    last_name: string
    nickname: string
    birthdate: string
    email?: string
    address?: string
    phone?: string
    reservation_driver_id?: number
    packages_cart_item_id: number | null
    parent_user_id?: number | null
}

export interface SetCustomerToReservationRequest {
    customer: BookingLoginCustomerInReservation
    reservation: Reservation
}

export interface SetCustomerToReservationNewRequest {
    customer: BookingLoginCustomerInReservationNew
    reservation: Reservation
}

export interface ExcelImportResponse {
    importState: {
        importCollection: []
        failureCollection: []
        importRows: number
        totalRows: number
    }
}

export class BookingLoginAction {
    static readonly SET_BOOKING_LOGIN_RESERVATION =
        'BookingLoginAction.SetReservation'
    static readonly REMOVE_PACKAGES_CART_ITEM_FROM_SELECTED_RESERVATION =
        'BookingLoginAction.RemovePackagesCartItemFromSelectedReservation'
    static readonly REMOVE_RESERVATION_DRIVER_FROM_SELECTED_RESERVATION =
        'BookingLoginAction.RemoveReservationDriverFromSelectedReservation'
    static readonly SET_BOOKING_LOGIN_SELECTED_RESERVATION_RESERVATION_DRIVER =
        'BookingLoginAction.SetSelectedReservationReservationDriver'
    static readonly SET_BOOKING_LOGIN_SELECTED_RESERVATION_PACKAGES_CART_ITEM =
        'BookingLoginAction.SetSelectedReservationPackagesCartItem'
    static readonly SET_BOOKING_LOGIN_USER = 'BookingLoginAction.SetUser'
    static readonly SET_BOOKING_LOGIN_RESERVATIONS =
        'BookingLoginAction.SetReservations'
    static readonly REMOVE_RESERVATION_FROM_LIST =
        'BookingLoginAction.RemoveReservationFromList'
    static readonly CLEAR_BOOKING_LOGIN = 'BookingLoginAction.ClearBookingLogin'
    static readonly PUSH_RESERVATION_NOTE =
        'BookingLoginAction.PushReservationNote'
    static readonly SET_DRIVER_FAMILY_ACCOUNTS =
        'BookingLoginAction.SetDriverFamilyAccounts'
    static readonly RESET = 'BookingLoginAction.Reset'

    static fetchCheckCustomerEmail = (
        email: string
    ): ((dispatch, getState) => Promise<any>) => {
        return async (dispatch, getState) => {
            try {
                const response: {
                    exists: boolean
                } = await ajax.post(
                    '/user/exists-email',
                    {
                        email: email,
                    },
                    {}
                )

                if (response.exists === true) {
                    console.log('fetchLoginCheck - true')
                    return Promise.resolve(response.exists)
                } else if (response.exists === false) {
                    console.log('fetchLoginCheck - false')
                    return Promise.resolve(response.exists)
                } else {
                    return Promise.reject('error')
                }
            } catch (error) {
                return Promise.reject(error)
            }
        }
    }

    static fetchSetCustomerToReservation = (
        data: SetCustomerToReservationRequest
    ): ((dispatch, getState) => Promise<Reservation>) => {
        return async (dispatch, getState) => {
            try {
                const response: {
                    reservation: Reservation
                    reason?: string
                } = await ajax.post(
                    '/online-venue/reservation/guests/' + data.reservation.id,
                    {
                        ...data.customer,
                    },
                    {}
                )

                if (response?.reservation) {
                    dispatch(
                        BookingLoginAction.setReservation(response.reservation)
                    )
                    return Promise.resolve(response.reservation)
                } else {
                    return Promise.reject(response.reason)
                }
            } catch (error) {
                return Promise.reject(error)
            }
        }
    }

    static fetchUpdateCustomerFromReservation = (
        data: UpdateCustomerFromReservationRequest
    ): ((dispatch, getState) => Promise<Reservation>) => {
        return async (dispatch, getState) => {
            try {
                const response: {
                    reservation: Reservation
                    reason?: string
                } = await ajax.post(
                    '/online-venue/reservation/guests/' + data.reservation.id,
                    {
                        ...data.customer,
                    },
                    {}
                )

                if (response?.reservation) {
                    dispatch(
                        BookingLoginAction.setReservation(response.reservation)
                    )
                    return Promise.resolve(response.reservation)
                } else {
                    return Promise.reject(response.reason)
                }
            } catch (error) {
                return Promise.reject(error)
            }
        }
    }

    static fetchSetCustomerToReservationNew = (
        data: SetCustomerToReservationNewRequest
    ): ((dispatch, getState) => Promise<ReservationDriver>) => {
        return async (dispatch, getState) => {
            try {
                const response: {
                    reservationDriver: ReservationDriver
                    reason?: string
                } = await ajax.post(
                    '/online-venue/reservation/guests/edit/' +
                        data.reservation.id,
                    {
                        ...data.customer,
                    },
                    {}
                )

                if (response?.reservationDriver) {
                    dispatch(
                        BookingLoginAction.setReservationDriverToSelectedReservation(
                            response.reservationDriver
                        )
                    )
                    return Promise.resolve(response.reservationDriver)
                } else {
                    return Promise.reject(response.reason)
                }
            } catch (error) {
                return Promise.reject(error)
            }
        }
    }

    static fetchUpdateCustomerFromReservationNew = (
        data: UpdateCustomerFromReservationNewRequest
    ): ((dispatch, getState) => Promise<ReservationDriver>) => {
        return async (dispatch, getState) => {
            try {
                const response: {
                    reservationDriver: ReservationDriver
                    reason?: string
                } = await ajax.post(
                    '/online-venue/reservation/guests/edit/' +
                        data.reservation.id,
                    {
                        ...data.customer,
                    },
                    {}
                )

                if (response?.reservationDriver) {
                    dispatch(
                        BookingLoginAction.setReservationDriverToSelectedReservation(
                            response.reservationDriver
                        )
                    )
                    return Promise.resolve(response.reservationDriver)
                } else {
                    return Promise.reject(response.reason)
                }
            } catch (error) {
                return Promise.reject(error)
            }
        }
    }

    static fetchAddOrEditCartItems = (
        reservation_id: number,
        reservation_driver_id: number,
        packages_cart_item_id: number,
        type: 'add' | 'remove'
    ) => {
        return async (dispatch, getState) => {
            try {
                const response: {
                    reservation: Reservation
                    reservationDriver: ReservationDriver
                } = await ajax.post(
                    '/online-venue/reservation/guests/' +
                        reservation_id +
                        '/cart-items',
                    {
                        type: type,
                        reservation_driver_id: reservation_driver_id,
                        packages_cart_item_id: packages_cart_item_id,
                    },
                    {}
                )

                if (type == 'remove') {
                    dispatch(
                        BookingLoginAction.removePackagesCartItemFromSelectedReservation(
                            packages_cart_item_id
                        )
                    )
                }

                if (
                    response.reservation &&
                    response.reservationDriver &&
                    response.reservationDriver.packagesCartItems &&
                    type == 'add'
                ) {
                    response.reservationDriver.packagesCartItems.map((item) =>
                        dispatch(
                            BookingLoginAction.setPackagesCartItemToSelectedReservation(
                                item
                            )
                        )
                    )
                    // dispatch(BookingLoginAction.setReservation(response.reservation));
                }
                console.log('fetchAddOrEditCartItems', response)
                return Promise.resolve(response)
            } catch (error) {
                return Promise.reject(error)
            }
        }
    }

    static fetchReservationInvitation = (
        data: ReservationInvitationRequest
    ): ((dispatch, getState) => Promise<any>) => {
        return async (dispatch, getState) => {
            try {
                const response: {
                    packagesCartItem: PackagesCartItem
                    reservationDriver: ReservationDriver
                } = await ajax.post(
                    '/online-venue/reservation/invitation-with-items/' +
                        data.reservation_id,
                    {
                        email: data.email,
                        packages_cart_item_id: data.packages_cart_item_id,
                    },
                    {}
                )

                console.log('fetchReservationInvitation', response)

                if (response?.reservationDriver) {
                    return Promise.resolve(response)
                } else {
                    return Promise.reject(response)
                }
            } catch (error) {
                console.log('fetchReservationInvitation - error', error)
                return Promise.reject(error)
            }
        }
    }

    static fetchReservationAssignDriver = (
        data: ReservationAssignDriverToItemRequest
    ): ((
        dispatch,
        getState
    ) => Promise<{
        reservationDriver: ReservationDriver
        packagesCartItem: PackagesCartItem
    }>) => {
        return async (dispatch, getState) => {
            try {
                const response: {
                    reservationDriver: ReservationDriver
                    packagesCartItem: PackagesCartItem
                } = await ajax.post(
                    '/online-venue/reservation/assign-to-items/' +
                        data.reservation_id,
                    {
                        driver_id: data.driver_id,
                        packages_cart_item_id: data.packages_cart_item_id,
                    },
                    {}
                )

                console.log('fetchReservationAssignDriver', response)

                console.log(response.reservationDriver)
                if (response.packagesCartItem) {
                    dispatch(
                        BookingLoginAction.setPackagesCartItemToSelectedReservation(
                            response.packagesCartItem
                        )
                    )
                }
                if (response?.reservationDriver) {
                    dispatch(
                        BookingLoginAction.setReservationDriverToSelectedReservation(
                            response.reservationDriver
                        )
                    )
                    return Promise.resolve(response)
                } else {
                    return Promise.reject(response)
                }
            } catch (error) {
                console.log('fetchReservationAssignDriver - error', error)
                return Promise.reject(error)
            }
        }
    }

    static fetchReservationInvitationGuest = (
        data: ReservationInvitationGuestRequest
    ): ((dispatch) => Promise<any>) => {
        return async (dispatch) => {
            try {
                const response: {
                    data: any
                } = await ajax.post(
                    '/online-venue/reservation/guest/' +
                        data.reservation_driver_guid,
                    {
                        ...data,
                    }
                )

                console.log('fetchReservationInvitationGuest', response)
                return Promise.resolve(response.data)

                // if (response?.reservation) {
                //     dispatch(BookingLoginAction.setReservation(response.reservation));
                //     return Promise.resolve(response.reservation);
                // } else {
                //     return Promise.reject(response.reason);
                // }
            } catch (error) {
                return Promise.reject(error)
            }
        }
    }

    static getReservationInvitationGuest = (
        reservation_driver_guid: string
    ): ((dispatch) => Promise<ReservationDriver>) => {
        return async (dispatch) => {
            try {
                const response: {
                    reservationDriver: ReservationDriver
                } = await ajax.get(
                    '/online-venue/reservation/guest/' + reservation_driver_guid
                )

                console.log('fetchReservationInvitationGuest', response)
                return Promise.resolve(response.reservationDriver)
            } catch (error) {
                return Promise.reject(error)
            }
        }
    }

    static fetchReservationImportGuestsFromFile = (
        data: ReservationImportGuestsFromFileRequest
    ): ((dispatch, getState) => Promise<ExcelImportResponse>) => {
        return async (dispatch, getState) => {
            try {
                const response: ExcelImportResponse = await ajax.post(
                    '/online-venue/reservation/guest-import/' +
                        data.reservation_id,
                    data.file,
                    {}
                )

                if (response.importState.failureCollection.length === 0) {
                    console.log(
                        'on resolve from fetchReservationImportGuestsFromFile',
                        response
                    )
                    return Promise.resolve(response)
                } else {
                    console.log(
                        'on reject from fetchReservationImportGuestsFromFile',
                        response
                    )
                    return Promise.reject(response)
                }
            } catch (error) {
                console.error(error)
                return Promise.reject(error)
            }
        }
    }

    static fetchReservationDetail = (reservation_id: number) => {
        return async (dispatch, getState) => {
            // return new Promise(async (resolve, reject) => {
            try {
                const response: {
                    reservation: Reservation
                } = await ajax.get(
                    '/online-venue/reservation/detail/' + reservation_id,
                    {}
                )

                if (response?.reservation) {
                    dispatch(
                        BookingLoginAction.setReservation(response.reservation)
                    )
                    return Promise.resolve(response)
                    // return resolve(response.reservation);
                } else {
                    dispatch(BookingLoginAction.clearReservations())
                    return Promise.reject('reservation_not_found')
                    // reject(response);
                }
            } catch (error) {
                return Promise.reject('reservation_error')
                // return reject(error);
            }
            // });
        }
    }

    static fetchSetReservationOfferStatus = (
        reservation_id: number,
        offerStatus: ReservationOfferStatus
    ): ((dispatch) => Promise<any>) => {
        return async (dispatch) => {
            try {
                const response: {
                    reservation: Reservation
                } = await ajax.post(
                    '/online-venue/reservation/seller/set_reservation_offer/' +
                        reservation_id,
                    { offer: offerStatus }
                )
                dispatch(
                    BookingLoginAction.setReservation(response.reservation)
                )
                return Promise.resolve(response)
            } catch (error) {
                console.error(error)
                return Promise.reject(error)
            }
        }
    }

    static fetchDeleteReservationOffer = (
        reservation_id: number
    ): ((dispatch) => Promise<any>) => {
        return async (dispatch) => {
            try {
                const response: {
                    reservation: Reservation
                } = await ajax.delete(
                    '/online-venue/reservation/seller/delete_reservation_offer/' +
                        reservation_id,
                    {}
                )
                dispatch(SellerAction.deleteReservation(response.reservation))
                return Promise.resolve(response)
            } catch (error) {
                return Promise.reject(error)
            }
        }
    }

    // TODO: v pripade potreby doupravit ked bude spojazdnena
    static fetchSetReservationOfferStatusReason = (
        reservation_id: number,
        offerStatus: ReservationOfferStatus,
        reason: string
    ): ((dispatch) => Promise<any>) => {
        return async (dispatch) => {
            try {
                const response: {
                    reservation: Reservation
                } = await ajax.post(
                    '/online-venue/reservation/seller/set_reservation_offer_with_reason/' +
                        reservation_id,
                    {
                        offer: offerStatus,
                        reason: reason,
                    }
                )
                dispatch(
                    BookingLoginAction.setReservation(response.reservation)
                )
                return Promise.resolve(response)
            } catch (error) {
                console.error(error)
                return Promise.reject(error)
            }
        }
    }

    static fetchSellerReservationDetail = (reservation_id: number) => {
        return async (dispatch, getState) => {
            try {
                const response: {
                    reservation?: Reservation
                    reason?: string
                } = await ajax.get(
                    '/online-venue/reservation/seller/detail/' + reservation_id,
                    {}
                )

                if (response?.reservation) {
                    let reservation = response.reservation

                    // Seller reservation details currently contain contact_id,
                    // but not the related contact object. Load that exact
                    // contact so the detail can display the persisted person
                    // data instead of guessing from the account's defaults.
                    if (reservation.contact_id && !reservation.contact) {
                        try {
                            const contactResponse: {
                                companyContact?: CompanyContact
                            } = await ajax.get(
                                `/admin/contact_companies/detail/${reservation.contact_id}`,
                                {}
                            )

                            if (
                                contactResponse?.companyContact?.id ===
                                reservation.contact_id
                            ) {
                                reservation = {
                                    ...reservation,
                                    contact: contactResponse.companyContact,
                                }
                            }
                        } catch (error) {
                            // Contact information is supplemental. A missing or
                            // inaccessible contact must not hide the reservation.
                            console.error(
                                'Failed to load reservation contact:',
                                error
                            )
                        }
                    }

                    dispatch(SellerAction.setReservation(reservation))
                    return Promise.resolve({ ...response, reservation })
                } else if (response?.reason) {
                    return Promise.resolve(response)
                } else {
                    dispatch(SellerAction.clearReservations())
                    return Promise.reject(response)
                }
            } catch (error) {
                return Promise.reject(error)
            }
        }
    }

    static fetchReservationsList = () => {
        return async (dispatch, getState) => {
            try {
                const response: {
                    reservations: Reservation[]
                } = await ajax.get('/online-venue/reservations/list', {})
                if (response?.reservations) {
                    dispatch(
                        BookingLoginAction.setReservations(
                            response.reservations
                        )
                    )
                    return Promise.resolve(response.reservations)
                } else {
                    return Promise.reject(response)
                }
            } catch (error) {
                return Promise.reject(error)
            }
        }
    }

    static fetchReservationsListForSeller = (seller_id: number) => {
        /**
         * @deprecated Use ReservationsAction.fetchReservationsListForSeller
         */
        return ReservationsAction.fetchReservationsListForSeller(seller_id)
    }

	/**
	 * @deprecated Not used
	 * @param seller_id
	 * @param data
	 */
    static fetchReservationsListForSellerWithPagination = (
        seller_id: number,
        data: ReservationsTableRequest
    ) => {
        return (dispatch, getState) => {
            return ajax
                .post(
                    // "/admin/reservations/table/all",
                    '/online-venue/reservation/seller/pagination',
                    {
                        hall_id: DEFAULT_HALL_ID,
                        per_page: data?.per_page,
                        page: data?.page,
                        search: data?.search && data.search,
                        // sort: data?.sort && data.sort,
                        // sort_by: data?.sort_by && data.sort_by,
                        sorting: data?.sorting,
                        seller_id: seller_id,
                    },
                    {}
                )
                .then(
                    (onResolve) => {
                        return Promise.resolve(onResolve.reservations)
                    },
                    (onReject) => {
                        console.log('fetch data error')
                        console.log(onReject)
                        return Promise.reject(onReject)
                    }
                )
                .catch((error) => {
                    if (error.name === 'CanceledError') {
                        console.log('Request aborted:', error.message)
                    } else {
                        console.error(error)
                    }
                    return Promise.reject(error)
                })
        }

        // return (dispatch, getState) => {
        //     return ajax
        //         .post(
        //             "/online-venue/reservation/seller/list",
        //             {
        //                 // TODO: odkomentovat ked bude api pripravene
        //                 // hall_id: DEFAULT_HALL_ID,
        //                 // per_page: data?.per_page,
        //                 // page: data?.page,
        //                 // search: data?.search && data.search,
        //                 // sort: data?.sort && data.sort,
        //                 // sort_by: data?.sort_by && data.sort_by,
        //                 seller_id: seller_id,
        //             },
        //             {
        //
        //             }
        //         )
        //         .then(
        //             onResolve => {
        //                 // TODO: ked bdue api pripravene odstranit premennu {data: ...}
        //                 return {data: onResolve.reservations};
        //             },
        //             onReject => {
        //                 console.log("fetch data error");
        //                 console.log(onReject);
        //             }
        //         )
        //         .catch(error => {
        //             if (error.name === "CanceledError") {
        //                 console.log("Request aborted:", error.message);
        //             } else {
        //                 console.error(error);
        //             }
        //             return error;
        //         });
        // };
    }

    static fetchReservationsLogout = () => {
        return async (dispatch) => {
            dispatch(BookingLoginAction.clearReservations())
            return dispatch(SessionAction.logout())
        }
    }

    static fetchRemoveReservationDriverFromReservation = (
        reservation_driver_id: number
    ) => {
        return async (dispatch, getState) => {
            try {
                const response: {
                    status: boolean
                } = await ajax.delete(
                    '/online-venue/reservation/guests/delete/' +
                        reservation_driver_id,
                    {}
                )
                if (response?.status) {
                    dispatch(
                        BookingLoginAction.removeReservationDriverFromSelectedReservation(
                            reservation_driver_id
                        )
                    )
                    return Promise.resolve(response)
                } else {
                    return Promise.reject(response)
                }
            } catch (e) {
                console.log('fetchRemoveReservationDriverFromReservation', e)
            }
        }
    }

    static clearReservations = () => {
        return createAction(BookingLoginAction.CLEAR_BOOKING_LOGIN, [])
    }

    static setDriverFamilyAccounts = (driver: Driver) => {
        return createAction(
            BookingLoginAction.SET_DRIVER_FAMILY_ACCOUNTS,
            driver
        )
    }

    static setReservationDriverToSelectedReservation = (
        reservationDriver: ReservationDriver
    ) => {
        return createAction(
            BookingLoginAction.SET_BOOKING_LOGIN_SELECTED_RESERVATION_RESERVATION_DRIVER,
            reservationDriver
        )
    }

    static setPackagesCartItemToSelectedReservation = (
        packagesCartItem: PackagesCartItem
    ) => {
        return createAction(
            BookingLoginAction.SET_BOOKING_LOGIN_SELECTED_RESERVATION_PACKAGES_CART_ITEM,
            packagesCartItem
        )
    }

    static removeReservationDriverFromSelectedReservation = (
        reservation_driver_id: number
    ) => {
        return createAction(
            BookingLoginAction.REMOVE_RESERVATION_DRIVER_FROM_SELECTED_RESERVATION,
            reservation_driver_id
        )
    }

    static removePackagesCartItemFromSelectedReservation = (
        packages_cart_item_id: number
    ) => {
        return createAction(
            BookingLoginAction.REMOVE_PACKAGES_CART_ITEM_FROM_SELECTED_RESERVATION,
            packages_cart_item_id
        )
    }

    static setReservations = (reservations: Reservation[]) => {
        return createAction(
            BookingLoginAction.SET_BOOKING_LOGIN_RESERVATIONS,
            reservations
        )
    }

    static removeReservationFromList = (reservation: Reservation) => {
        return createAction(
            BookingLoginAction.REMOVE_RESERVATION_FROM_LIST,
            reservation
        )
    }
    static setUser = (user: Driver) => {
        return createAction(BookingLoginAction.SET_BOOKING_LOGIN_USER, user)
    }

    static setReservation = (reservation: Reservation) => {
        return createAction(
            BookingLoginAction.SET_BOOKING_LOGIN_RESERVATION,
            reservation
        )
    }

    static pushReservationNote = (note: ReservationNote) => {
        return createAction(BookingLoginAction.PUSH_RESERVATION_NOTE, note)
    }

    static fetchExportReservationsXlsx = (
        seller_id: number,
        data: ReservationsTableRequest & { export?: 'xlsx' | 'csv' | 'pdf' }
    ) => {
        return ReservationsAction.fetchExportReservationsXlsx({
            ...data,
            seller_id,
            export: data.export ?? 'csv',
        })
    }

    static resetBookingLoginState = () => {
        return createAction(BookingLoginAction.RESET, null)
    }
}

export default BookingLoginAction
